import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

export interface ProviderCostChartProps {
  data: Record<string, { cost: number; requests: number }>;
  className?: string;
}

// Color palette for providers
const PROVIDER_COLORS: Record<string, string> = {
  openrouter: '#3b82f6',
  openai: '#10b981',
  anthropic: '#8b5cf6',
  google: '#f59e0b',
  deepseek: '#06b6d4',
  meta: '#ec4899',
  default: '#6b7280',
};

const getProviderColor = (provider: string): string => {
  const lowerProvider = provider.toLowerCase();
  for (const [key, color] of Object.entries(PROVIDER_COLORS)) {
    if (lowerProvider.includes(key)) {
      return color;
    }
  }
  return PROVIDER_COLORS.default;
};

const formatProviderName = (provider: string): string => {
  // Shorten long provider names for display
  if (provider.length > 15) {
    return provider.split('/').pop() || provider.substring(0, 12) + '...';
  }
  return provider;
};

const formatCurrency = (value: number): string => {
  if (value >= 1) {
    return `$${value.toFixed(2)}`;
  }
  if (value >= 0.01) {
    return `$${value.toFixed(3)}`;
  }
  return `$${value.toFixed(4)}`;
};

interface ChartDataItem {
  name: string;
  fullName: string;
  cost: number;
  requests: number;
  color: string;
}

export const ProviderCostChart: React.FC<ProviderCostChartProps> = ({
  data,
  className = '',
}) => {
  const chartData: ChartDataItem[] = Object.entries(data)
    .map(([provider, stats]) => ({
      name: formatProviderName(provider),
      fullName: provider,
      cost: stats.cost,
      requests: stats.requests,
      color: getProviderColor(provider),
    }))
    .sort((a, b) => b.cost - a.cost)
    .slice(0, 8); // Show top 8 providers

  if (chartData.length === 0) {
    return (
      <div className={`flex h-64 items-center justify-center text-gray-500 ${className}`}>
        No provider data available
      </div>
    );
  }

  return (
    <div className={`h-64 ${className}`}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
          <XAxis
            type="number"
            tickFormatter={(value) => formatCurrency(value)}
            fontSize={12}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={100}
            fontSize={12}
            tick={{ fill: '#6b7280' }}
          />
          <Tooltip
            formatter={(value: number | string | undefined) => [
              formatCurrency(Number(value ?? 0)),
              'Cost',
            ]}
            labelFormatter={(label, payload) => {
              if (payload && payload[0]) {
                const item = payload[0].payload as ChartDataItem;
                return `${item.fullName} (${item.requests} requests)`;
              }
              return label;
            }}
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
            }}
          />
          <Bar dataKey="cost" radius={[0, 4, 4, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

ProviderCostChart.displayName = 'ProviderCostChart';

export default ProviderCostChart;
